import { z } from 'zod';

// =============================================================================
// API Response Schemas — Runtime validation for API boundary
// =============================================================================
// These Zod schemas validate API responses at runtime to catch backend contract
// changes early. Use `safeParse` in api.ts to validate responses before they
// propagate through the app.
// =============================================================================

// ---------------------------------------------------------------------------
// Generic API Response wrapper
// ---------------------------------------------------------------------------

export function apiResponseSchema<T extends z.ZodType>(dataSchema: T) {
  return z.object({
    success: z.boolean(),
    data: dataSchema.optional(),
    error: z.string().optional(),
    message: z.string().optional(),
  });
}

// ---------------------------------------------------------------------------
// Core Enums
// ---------------------------------------------------------------------------

export const taxReturnStatusSchema = z.enum([
  'scheduled',
  'pending_resolution',
  'documents_pending',
  'ready_to_prepare',
  'in_preparation',
  'in_review',
  'needs_corrections',
  'ready_for_signature',
  'waiting_for_payment',
  'ready_for_efile',
  'e_filed',
  'completed',
  'inactive',
]);

export const filingTypeSchema = z.enum(['single', 'joint', 'business']);

export const efileStatusSchema = z.enum(['pending', 'submitted', 'accepted', 'rejected']);

export const signatureStatusSchema = z.enum(['pending', 'sent', 'signed']);

export const invoiceInfoStatusSchema = z.enum(['not_generated', 'generated', 'paid']);

export const inquiryStatusSchema = z.enum([
  'new', 'in_progress', 'waiting_for_client', 'resolved', 'closed',
]);

export const drakeMatchStatusSchema = z.enum([
  'pending_review', 'matched', 'multiple_matches', 'no_match',
]);

// ---------------------------------------------------------------------------
// Nested object schemas
// ---------------------------------------------------------------------------

export const statusChangeSchema = z.object({
  fromStatus: taxReturnStatusSchema.nullable(),
  toStatus: taxReturnStatusSchema,
  changedAt: z.string(),
  changedBy: z.string().optional(),
  changedByName: z.string().optional(),
  notes: z.string().optional(),
});

export const invoiceInfoSchema = z.object({
  id: z.string().optional(),
  status: invoiceInfoStatusSchema,
  amount: z.number().optional(),
  description: z.string().optional(),
  generatedDate: z.string().optional(),
  paidDate: z.string().optional(),
  invoiceNumber: z.string().optional(),
  paymentUrl: z.string().optional(),
  xeroUrl: z.string().optional(),
});

export const taxReturnNoteSchema = z.object({
  id: z.string(),
  content: z.string(),
  createdAt: z.string(),
  createdBy: z.string().optional(),
  createdByName: z.string().optional(),
  isImportant: z.boolean(),
});

export const extractedInvoiceDataSchema = z.object({
  totalAmount: z.number().optional(),
  lineItems: z.array(z.object({
    description: z.string(),
    amount: z.number(),
    quantity: z.number(),
  })).optional(),
});

export const taxReturnExtractedDataSchema = z.object({
  refundOrPayment: z.enum(['refund', 'payment', 'zero']),
  amount: z.number(),
  agi: z.number().optional(),
  totalTax: z.number().optional(),
  totalIncome: z.number().optional(),
  filingStatus: z.string().optional(),
  extractedAt: z.string(),
  sourceFileName: z.string().optional(),
});

// ---------------------------------------------------------------------------
// TaxReturn — the central entity
// ---------------------------------------------------------------------------

export const taxReturnSchema = z.object({
  id: z.string(),
  appointmentId: z.string(),
  linkedAppointmentIds: z.array(z.string()),
  taxYear: z.number(),
  returnType: z.enum(['individual', 'business']),
  filingType: filingTypeSchema,
  clientId: z.string(),
  clientName: z.string(),
  clientEmail: z.string().optional(),
  clientPhone: z.string().optional(),
  contactId: z.string().nullable().optional(),
  contactName: z.string().nullable().optional(),
  spouseId: z.string().nullable().optional(),
  spouseName: z.string().nullable().optional(),
  spouseEmail: z.string().nullable().optional(),
  spousePhone: z.string().nullable().optional(),
  linkedReturnIds: z.array(z.string()),
  parentReturnId: z.string().optional(),
  businessOwnerId: z.string().optional(),
  businessOwnerName: z.string().optional(),
  createdFrom: z.enum(['appointment', 'manual', 'linked']),
  appointmentType: z.enum(['in-person', 'virtual', 'drop-off']),
  scheduledDate: z.string(),
  services: z.string(),
  status: taxReturnStatusSchema,
  statusHistory: z.array(statusChangeSchema),
  allDocumentsReceived: z.boolean(),
  missingDocuments: z.array(z.string()),
  documentsReceivedDate: z.string().optional(),
  changesNeededAfterReview: z.boolean(),
  reviewNotes: z.string().optional(),
  reviewCompletedDate: z.string().optional(),
  efileStatus: efileStatusSchema,
  efileSubmittedDate: z.string().optional(),
  efileAcceptedDate: z.string().optional(),
  efileRejectionReason: z.string().optional(),
  signatureStatus: signatureStatusSchema,
  signatureRequestId: z.string().optional(),
  signatureSentDate: z.string().optional(),
  signatureCompletedDate: z.string().optional(),
  signatureFormType: z.enum(['8879', '8879-C', '8879-S', '8879-PE']).optional(),
  authorizedSignerName: z.string().optional(),
  authorizedSignerEmail: z.string().optional(),
  clientInvoice: invoiceInfoSchema,
  additionalInvoices: z.array(invoiceInfoSchema).optional(),
  totalInvoicedAmount: z.number().optional(),
  preparerBillAmount: z.number().optional(),
  invoiceId: z.string().optional(),
  preparerBillId: z.string().optional(),
  cashPaymentAmount: z.number().optional(),
  cashPaymentDate: z.string().optional(),
  cashPaymentReference: z.string().optional(),
  cashPaymentRecordedBy: z.string().optional(),
  preparerId: z.string(),
  preparerName: z.string(),
  preparerEmail: z.string().optional(),
  reviewerId: z.string().optional(),
  reviewerName: z.string().optional(),
  reviewerEmail: z.string().optional(),
  lastFourEin: z.string().nullable().optional(),
  extractedData: taxReturnExtractedDataSchema.optional(),
  extractedInvoiceData: extractedInvoiceDataSchema.optional(),
  notes: z.array(taxReturnNoteSchema),
  conversationId: z.string().optional(),
  department: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  completedAt: z.string().optional(),
  lastSyncedAt: z.string().optional(),
  appointmentCancelledAt: z.string().optional(),
  cancellationReason: z.string().optional(),
  isCancelled: z.boolean().optional(),
  searchableAliases: z.array(z.string()).default([]),
  reviewStatus: z.enum([
    'not_started', 'in_progress', 'completed',
    'completed_pending_payment', 'changes_required_preparer', 'changes_required_taxpayer'
  ]).default('not_started'),
});

// ---------------------------------------------------------------------------
// TaxDocument
// ---------------------------------------------------------------------------

export const taxDocumentSchema = z.object({
  id: z.string(),
  taxReturnId: z.string(),
  fileName: z.string(),
  fileType: z.string(),
  contentType: z.string(),
  fileSize: z.number(),
  blobUrl: z.string(),
  blobName: z.string(),
  documentType: z.string(),
  documentYear: z.number().optional(),
  issuerName: z.string().optional(),
  uploadSource: z.enum(['portal', 'email', 'staff', 'zoho_sign']),
  uploadedBy: z.string().optional(),
  uploadedByName: z.string().optional(),
  uploadedAt: z.string(),
  emailFrom: z.string().optional(),
  emailSubject: z.string().optional(),
  emailReceivedAt: z.string().optional(),
  signatureRequestId: z.string().optional(),
  signedAt: z.string().optional(),
});

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

export const dashboardStatsSchema = z.object({
  totalReturns: z.number(),
  returnsByStatus: z.record(z.string(), z.number()),
  pendingDocuments: z.number(),
  pendingSignatures: z.number(),
  pendingPayments: z.number(),
  efileRejections: z.number(),
  completedThisWeek: z.number(),
  unassignedDropoffs: z.number(),
  returnsByPreparer: z.record(z.string(), z.number()),
});

// ---------------------------------------------------------------------------
// Validation helper
// ---------------------------------------------------------------------------

/**
 * Validate an API response at runtime. In development, logs warnings for
 * validation failures. In production, silently passes through to avoid
 * breaking the app on minor schema mismatches.
 */
export function validateApiResponse<T>(
  data: unknown,
  schema: z.ZodType<T>,
  context: string,
): T {
  const result = schema.safeParse(data);
  if (!result.success) {
    const message = `API validation warning [${context}]: ${result.error.issues.map(i => `${i.path.join('.')}: ${i.message}`).join(', ')}`;
    if (import.meta.env.DEV) {
      console.warn(message);
    }
    // Pass through the original data in production — don't break the app
    return data as T;
  }
  return result.data;
}
